import React from 'react';

import { Checkmark as CheckmarkIcon } from 'src/svg/checkmark';
import { Settings as SettingsIcon } from 'src/svg/settings';
import { ImageBannerStepsStatus } from './expandable-step.constants';

import {
  CheckmarkContainer,
  ExpandableStepBadge,
  ExpandableStepContent,
  ExpandableStepDescription,
  ExpandableStepEdit,
  ExpandableStepHeader,
  ExpandableStepLabels,
  ExpandableStepTitle,
  ExpandableStepWrapper,
} from './expandable-step.styles';

type ExpandableStepProps = {
  children: JSX.Element;
  description?: string;
  expanded: boolean;
  isEditDisabled: boolean;
  onEditClick: (event: React.MouseEvent<HTMLElement>) => void;
  status: ImageBannerStepsStatus;
  stepNumber: number;
  title: string;
  isReadOnly: boolean;
  hasPassedStep: boolean;
};

const TEST_ID_IMAGE_BANNER_EXPANDABLE_STEPS_WRAPPER = 'image-banner-expandable-steps-wrapper';

export function ExpandableStep(props: ExpandableStepProps): JSX.Element {
  const {
    children,
    description,
    expanded,
    isEditDisabled,
    onEditClick,
    status,
    stepNumber,
    title,
    isReadOnly,
    hasPassedStep,
  } = props;

  const disabled = status === 'incomplete' || isEditDisabled;

  function handleEditClick(event: any): void {
    event.preventDefault();
    if (!isEditDisabled) {
      onEditClick(event);
    }
  }

  function renderEditOrViewType(): JSX.Element {
    if (isReadOnly) {
      return <span>View</span>;
    }
    return (
      <>
        <SettingsIcon height='19' width='19' />
        <span>Edit</span>
      </>
    );
  }

  return (
    <ExpandableStepWrapper expanded={expanded} data-testid={TEST_ID_IMAGE_BANNER_EXPANDABLE_STEPS_WRAPPER}>
      <ExpandableStepHeader expanded={expanded} status={status} id={`step_${stepNumber}`} disabled={disabled}>
        <ExpandableStepBadge status={status}>
          {status === 'complete' ? (
            <CheckmarkContainer>
              <CheckmarkIcon height='10' width='14' viewBox='0 0 9 7' />
            </CheckmarkContainer>
          ) : (
            <span>{stepNumber}</span>
          )}
        </ExpandableStepBadge>
        <ExpandableStepLabels>
          <ExpandableStepTitle>{title}</ExpandableStepTitle>
          {description && <ExpandableStepDescription>{description}</ExpandableStepDescription>}
        </ExpandableStepLabels>
        {hasPassedStep && (
          <ExpandableStepEdit disabled={isEditDisabled} onClick={handleEditClick}>
            {renderEditOrViewType()}
          </ExpandableStepEdit>
        )}
      </ExpandableStepHeader>
      <ExpandableStepContent
        expanded={expanded}
        data-cy={`${title}-step-content`}
        data-test={`${title}-step-content`}
        data-testid={`${title}-step-content`}
      >
        {expanded && children}
      </ExpandableStepContent>
    </ExpandableStepWrapper>
  );
}
